import React from 'react';
import {
  Bullseye,
  EmptyState,
  EmptyStateBody,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { MapMarkerAltIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { countries } from 'countries-list';
import type { VerdaLocation } from '../../../types/models/Verda';
import type { VerdaLocationsState } from '../../../hooks/useAdminVerda';
import { VerdaErrorAlert, VerdaLastFetched, VerdaRefreshButton } from './VerdaTabParts';

/** "Finland (FI)"; the bare code when `countries-list` doesn't know it. */
const countryLabel = (code: string): string => {
  const name = (countries as Record<string, { name: string }>)[code]?.name;
  return name ? `${name} (${code})` : code;
};

/** "Yes (10.128.16.0/20)" / "Yes" / "No", or "—" when Verda didn't say (an undocumented field). */
const privateNetworksLabel = (location: VerdaLocation): string => {
  if (location.private_networks_enabled === null) {
    return '—';
  }
  if (!location.private_networks_enabled) {
    return 'No';
  }
  return location.private_network_auto_subnet !== null ? `Yes (${location.private_network_auto_subnet})` : 'Yes';
};

/**
 * Admin › Verda › Locations — Verda's datacenter locations, in Verda's order. A compact table with no
 * search or pagination: there are only a handful of rows (3 on 2026-09-27), an explicit opt-out
 * from the list-table rule. The read is owned by the page (shared with the Availability tab's headers).
 */
const VerdaLocationsTab: React.FunctionComponent<VerdaLocationsState> = ({
  locations,
  fetchedAt,
  loading,
  error,
  refetch,
}) => {
  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="locations" message={error} />;
    }
    // No data and no error = loading, including the render before the page's lazy fetch starts.
    if (!locations) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the Verda locations" />
        </Bullseye>
      );
    }
    if (locations.length === 0) {
      return (
        <EmptyState titleText="No locations" icon={MapMarkerAltIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Verda returned no datacenter locations.</EmptyStateBody>
        </EmptyState>
      );
    }
    return (
      <Table aria-label="Verda locations" variant="compact">
        <Thead>
          <Tr>
            <Th>Code</Th>
            <Th>Name</Th>
            <Th>Country</Th>
            <Th>Private networks</Th>
          </Tr>
        </Thead>
        <Tbody>
          {locations.map((location) => (
            <Tr key={location.code}>
              <Td dataLabel="Code">
                <span className="pf-v6-u-font-family-monospace">{location.code}</span>
              </Td>
              <Td dataLabel="Name">{location.name}</Td>
              <Td dataLabel="Country">{countryLabel(location.country_code)}</Td>
              <Td dataLabel="Private networks">{privateNetworksLabel(location)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refetch} what="locations" />
          </ToolbarItem>
          {fetchedAt !== null && !error && (
            <ToolbarItem alignSelf="center">
              <VerdaLastFetched fetchedAt={fetchedAt} />
            </ToolbarItem>
          )}
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
    </>
  );
};

export default VerdaLocationsTab;
